<template>
    <div v-if="filteredEvents.length > 0">
        <div
            class="box is-condensed is-flex is-flex-direction-screen-based is-flex-align-items-screen-based"
            v-for="event in filteredEvents"
            :key="event.id"
        >
            <div class="is-flex-grow-1 is-flex is-align-items-center">
                <EventThumbnail
                    :thumbnail-image-url="event.thumbnailImage"
                    :full-name="event.fullName"
                    size="64x64"
                    :url="getEventAdminUrl(event.id)"
                />

                <div class="ml-3">
                    <p>
                        <a :href="getEventAdminUrl(event.id)">
                            {{ event.fullName }}
                        </a>
                        <PrivateEventBadge v-if="event.isPrivate" />
                    </p>
                    <p class="has-text-grey-light">
                        <span class="icon">
                            <i class="fas fa-calendar-alt"></i>
                        </span>
                        <span>
                            {{ convertToTimezone(event.eventDate.startDate, event.timezone, "yyyy-MM-dd") }}
                        </span>
                    </p>
                </div>
            </div>
            <div class="mt-2-mobile">
                <button
                    v-if="event.federationId === null"
                    :disabled="!event.canMoveToFederation"
                    class="button is-small is-info"
                    @click="openMigrateToFederationModal(event.id)"
                >
                    <span class="icon">
                        <i class="fas fa-wrench"></i>
                    </span>
                    <span>{{ translate("federation.migrate_to_federation") }}</span>
                </button>
            </div>
        </div>

        <div v-if="hasMoreEvents" class="has-text-centered mt-3 mb-4">
            <button class="button" @click="emit('load-all')">
                <span class="icon">
                    <i class="fas fa-angle-down"></i>
                </span>
                <span>
                    {{ translate("page.events.load_all_drafts", false, [String(matchingEvents.length)]) }}
                </span>
            </button>
        </div>
    </div>
    <div v-else class="mb-3">
        <p class="has-text-info">{{ translate("page.events.no_events") }}</p>
    </div>
</template>

<script setup lang="ts">
    import objectMatcher from "../Common/objectMatcher";
    import { computed } from "vue";
    import { convertToTimezone } from "../Common/dateTimeUtils";
    import urls from "../Urls/urls";
    import { translate } from "../Common/translations";
    import { CompactEvent } from "../Common/types";
    import { manageEventsAppEventBus, OPEN_MIGRATE_TO_FEDERATION_MODAL } from "./manageEventsApp";
    import EventThumbnail from "../Common/Events/EventThumbnail.vue";
    import PrivateEventBadge from "../Common/PrivateEventBadge.vue";

    const props = defineProps<{
        events: CompactEvent[];
        searchQuery: string;
        limit?: number;
    }>();

    const matchingEvents = computed(() => {
        if (!props.searchQuery) {
            return props.events;
        }

        return props.events.filter((event: CompactEvent) => {
            return objectMatcher.matchString(props.searchQuery, event.fullName);
        });
    });

    // Searching should look across all events, not just the initial preview slice.
    const filteredEvents = computed(() => {
        if (!props.limit || props.searchQuery) {
            return matchingEvents.value;
        }

        return matchingEvents.value.slice(0, props.limit);
    });

    const hasMoreEvents = computed(() => {
        return !!props.limit && !props.searchQuery && matchingEvents.value.length > props.limit;
    });

    const emit = defineEmits<{ "load-all": [] }>();

    function getEventAdminUrl(eventId) {
        return urls.eventAdmin().eventSummaryForAdmin(eventId);
    }

    // Opening migration modal
    function openMigrateToFederationModal(eventId: number) {
        manageEventsAppEventBus.$emit(OPEN_MIGRATE_TO_FEDERATION_MODAL, eventId);
    }
</script>

<style scoped></style>
